import React, { useEffect, useState } from "react"
import analytics, { ANALYTICS_EVENT_NAMES } from "@love-irish/analytics"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@love-irish/ui-components/dialog"

import { useModalAnalytics } from "@love-irish/analytics"
import { lookupGrammar, GrammarEntry, GrammarSlide } from "./grammar_glossary"
import { GrugTip } from "./grug_tip"

/**
 * The paged reference renderer: one section per page, with Back/Next and a jump-to table of contents
 * on the intro page. A page-view event fires whenever the visible page changes (Next/Back or a jump),
 * so we can see how far through a reference people read. One place that renders a premade slide list,
 * shared by the inline grammar-term annotations and standalone references.
 */
export const GrammarSections: React.FC<{
  entry: GrammarEntry
  analyticsName?: string
  /** Render the entry title in a DialogHeader (true, standalone) or as an inline heading (false, when
   *  embedded in a modal that already owns the header, e.g. the Irish language modal). */
  renderHeader?: boolean
  /** Optionally control the current page. When provided, the parent owns paging (so its breadcrumb can
   *  jump back to the chapter root); omit for standalone (uncontrolled) use. */
  page?: number
  onPageChange?: (page: number) => void
  /** Session id from the enclosing modal, to stitch these page views to that open. */
  modalSessionId?: string | null
  /** Force every slide stacked on one page (no pager), regardless of `entry.singlePage`. Used by the
   *  standalone cheat-sheet pages so a whole chapter reads at a glance. */
  singlePage?: boolean
  /** Fire the MODAL_PAGE_VIEWED analytics as the page changes. Default true (in the modal); pass false
   *  on standalone pages, where a "modal page viewed" event is the wrong signal. */
  trackPageViews?: boolean
}> = ({
  entry,
  analyticsName = "grammar_reference",
  renderHeader = true,
  page: controlledPage,
  onPageChange,
  modalSessionId,
  singlePage,
  trackPageViews = true,
}) => {
  const [internalPage, setInternalPage] = useState(0)
  const page = controlledPage ?? internalPage
  const setPage = onPageChange ?? setInternalPage
  const total = entry.slides.length
  const current = entry.slides[page] ?? entry.slides[0]
  const stacked = singlePage ?? entry.singlePage

  // Restart at the first page when a different reference is shown – uncontrolled only. A controlled
  // parent (the language modal) owns paging and may deep-link past the root, so it must not be reset.
  useEffect(() => {
    if (onPageChange === undefined) setInternalPage(0)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entry.title])

  // A page-view event on every page change (incl. the first page on open), carrying which section it
  // is, so the funnel shows how far people read.
  useEffect(() => {
    if (!trackPageViews) return
    analytics.track(ANALYTICS_EVENT_NAMES.MODAL_PAGE_VIEWED, {
      name: analyticsName,
      reference: entry.title,
      page,
      section: current?.nav?.label ?? current?.heading,
      modalSessionId,
    })
    // Only re-fire when the page (or reference) changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, entry.title, modalSessionId, trackPageViews])

  // Stacked view: every section on one page, no pager (short prose entries, or forced by the caller).
  if (stacked) {
    return (
      <>
        {renderHeader && (
          <DialogHeader>
            <DialogTitle>{entry.title}</DialogTitle>
            {/* Screen-reader description so the dialog is labelled without duplicating a heading. */}
            <DialogDescription className="sr-only">
              {`${entry.title} reference`}
            </DialogDescription>
          </DialogHeader>
        )}
        <div className="space-y-5">
          {entry.slides.map((slide, index) => (
            <section key={index}>
              {slide.heading && (
                <h3 className="mb-1 text-sm font-semibold">{slide.heading}</h3>
              )}
              <GrammarSection slide={slide} />
            </section>
          ))}
        </div>
      </>
    )
  }

  return (
    <>
      {renderHeader ? (
        <DialogHeader>
          <DialogTitle>{entry.title}</DialogTitle>
          {current.heading && (
            <DialogDescription>{current.heading}</DialogDescription>
          )}
        </DialogHeader>
      ) : (
        current.heading && (
          <h3 className="text-base font-semibold">{current.heading}</h3>
        )
      )}

      {/* The intro page lists a jump-to table of contents; other pages just step. */}
      {page === 0 && <ReferenceNav entry={entry} onJump={setPage} />}

      <GrammarSection slide={current} />

      {total > 1 && (
        <div className="mt-2 flex items-center justify-between text-sm">
          <button
            type="button"
            disabled={page === 0}
            onClick={() => setPage(page - 1)}
            className="disabled:opacity-40"
          >
            Back
          </button>
          <span className="text-muted-foreground">
            {page + 1} / {total}
          </span>
          <button
            type="button"
            disabled={page === total - 1}
            onClick={() => setPage(page + 1)}
            className="disabled:opacity-40"
          >
            Next
          </button>
        </div>
      )}
    </>
  )
}

/** One page's body: prose, worked examples (as kbd code), and any rich JSX content. The heading is
 *  shown in the dialog description, so it is not repeated here. */
const GrammarSection: React.FC<{ slide: GrammarSlide }> = ({ slide }) => (
  <div className="text-sm">
    {slide.body && <p>{slide.body}</p>}

    {slide.examples && slide.examples.length > 0 && (
      <ul className="mt-1 space-y-1">
        {slide.examples.map((example, exampleIndex) => (
          <li key={exampleIndex}>
            {/* kbd so the Irish form reads as a highlighted code token. */}
            <kbd className="rounded border border-secondary-200 bg-secondary-50 px-1.5 py-0.5 font-mono text-xs not-italic text-foreground shadow-none">
              {example.irish}
            </kbd>{" "}
            <span className="text-muted-foreground">({example.english})</span>
          </li>
        ))}
      </ul>
    )}

    {/* Rich content (chart tables, transforms, media) supplied as JSX from the config. */}
    {slide.content && <div className="mt-2">{slide.content}</div>}

    {/* A simple, in-character Grug tip for the section. */}
    {slide.tip && <GrugTip>{slide.tip}</GrugTip>}
  </div>
)

/** Table-of-contents cards on the intro page: one per section with `nav`, jumping to that page. Each
 *  card is an icon (left) with the label, a one-line description, and a short example on the right. */
const ReferenceNav: React.FC<{
  entry: GrammarEntry
  onJump: (index: number) => void
}> = ({ entry, onJump }) => {
  const jumps = entry.slides
    .map((slide, index) => ({ nav: slide.nav, index }))
    .filter((jump) => jump.index > 0 && jump.nav)
  if (jumps.length === 0) return null

  return (
    <div
      className="grid grid-cols-1 gap-2 sm:grid-cols-2"
      data-testid="reference-nav"
    >
      {jumps.map(({ nav, index }) => (
        <button
          key={index}
          type="button"
          onClick={() => onJump(index)}
          className="flex items-start gap-4 rounded-lg border border-secondary-200 p-3 text-left hover:border-primary-300 hover:bg-secondary-50"
        >
          {nav!.icon && (
            <span className="mt-0.5 shrink-0 text-muted-foreground">
              {nav!.icon}
            </span>
          )}
          <span className="min-w-0 flex-1">
            <span className="block text-sm font-medium">{nav!.label}</span>
            {nav!.description && (
              <span className="block text-xs text-muted-foreground">
                {nav!.description}
              </span>
            )}
            {nav!.example && (
              <span className="block text-xs italic text-muted-foreground">
                {nav!.example}
              </span>
            )}
          </span>
        </button>
      ))}
    </div>
  )
}

/**
 * Controlled reference modal: pass a premade `entry` (a list of pages) and it pages through them.
 * The reusable core that both the grammar-term annotations and standalone references open.
 * `useModalAnalytics` fires modal.opened; the pager fires modal.page_viewed per page.
 */
export const GrammarReferenceModal: React.FC<{
  entry: GrammarEntry
  open: boolean
  onOpenChange: (open: boolean) => void
}> = ({ entry, open, onOpenChange }) => {
  useModalAnalytics("grammar_reference", open)

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* Wider + scrollable so a page's chart tables (vowels, séimhiú, urú, ...) have room. */}
      <DialogContent className="max-h-[85vh] gap-3 overflow-y-auto sm:max-w-2xl">
        <GrammarSections entry={entry} />
      </DialogContent>
    </Dialog>
  )
}

/**
 * A trigger that opens the reference modal for a premade entry, by glossary key (e.g. "pronunciation")
 * or a passed `entry`. Renders the children plainly when the key resolves to nothing, so it is safe to
 * wrap any label.
 *
 * ```tsx
 * <GrammarReference referenceKey="pronunciation">Pronunciation guide</GrammarReference>
 * ```
 */
export const GrammarReference: React.FC<{
  referenceKey?: string
  entry?: GrammarEntry
  className?: string
  children: React.ReactNode
}> = ({ referenceKey, entry, className, children }) => {
  const [open, setOpen] = useState(false)
  const resolved = entry ?? lookupGrammar(referenceKey)
  if (!resolved) return <>{children}</>

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={
          className ??
          "cursor-help border-0 bg-transparent p-0 text-inherit underline decoration-dotted underline-offset-2"
        }
      >
        {children}
      </button>
      {/* Mount the modal only once opened: a closed trigger stays a plain button, so rendering it doesn't
          pull in the modal's hooks (useModalAnalytics) – keeps it cheap and test-friendly wherever a guide
          link is dropped. */}
      {open && (
        <GrammarReferenceModal entry={resolved} open onOpenChange={setOpen} />
      )}
    </>
  )
}

export default GrammarReference
